import { from as observableFrom, of as observableOf, throwError as observableThrowError, BehaviorSubject, ReplaySubject, combineLatest, } from "rxjs";
import { finalize, first, last, map, mergeAll, mergeMap, tap, distinctUntilChanged, timeoutWith, catchError, } from "rxjs/operators";
import { CacheService } from "./CacheService";
import { LoadingService } from "./LoadingService";
//import { PanService } from "./PanService";
import { PlayService } from "./PlayService";
import { APIWrapper } from "../api/APIWrapper";
import { DataProviderBase } from "../api/DataProviderBase";
import { Infra3DDataProvider as Infra3DDataProvider_v2 } from "../api/Infra3DDataProvider_v2";
import { Infra3DDataProvider as Infra3DDataProvider_v3 } from "../api/Infra3DDataProvider_v3";
import { AbortInfra3DSDKError } from "../error/AbortInfra3DSDKError";
import { Graph } from "../graph/Graph";
import { GraphService } from "../graph/GraphService";
import { Node } from "../graph/Node";
import { EdgeDirection } from "../graph/edge/EdgeDirection";
import { StateService } from "../state/StateService";
import { CameraService } from "./CameraService";
import { CampaignService } from "./CampaignService";
import { LookAtService } from "./LookAtService";
import { JumpNodeCalculator } from "../jump/JumpNodeCalculator";
import { JumpMode } from "../jump/JumpMode";
import { Spatial } from "../geo/Spatial";
function isIInfra3DDataProviderOptionsV2(dataProvider) {
    return (dataProvider != null &&
        dataProvider.host !== undefined &&
        dataProvider.collection !== undefined &&
        dataProvider.framer_version == "2");
}
function isIInfra3DDataProviderOptionsV3(dataProvider) {
    return (dataProvider != null &&
        dataProvider.host !== undefined &&
        dataProvider.collection !== undefined &&
        dataProvider.framer_version == "3");
}
function isDataProviderBase(frameProvider) {
    return frameProvider instanceof DataProviderBase;
}
export class Navigator {
    constructor(options, api, graphService, loadingService, stateService, cacheService, playService, cameraService, campaignService, lookAtService) {
        this._needsResetPanoOrientation = false;
        if (api) {
            this._api = api;
        }
        else if (isIInfra3DDataProviderOptionsV2(options.frameProvider)) {
            this._api = new APIWrapper(new Infra3DDataProvider_v2(options.frameProvider, options.authObject, options.lidarProvider));
        }
        else if (isIInfra3DDataProviderOptionsV3(options.frameProvider)) {
            this._api = new APIWrapper(new Infra3DDataProvider_v3(options.frameProvider, options.authObject, options.lidarProvider, options.projectScope));
        }
        else if (isDataProviderBase(options.frameProvider)) {
            this._api = new APIWrapper(options.frameProvider);
        }
        else {
            throw new Error(`Invalid type: 'frameProvider' must be a { host: string, collection: string, framer_version: string, authObject: { apiKey: string, accessToken: string} } or an object instance extending the DataProvderBase class.`);
        }
        // NOTE: (3)
        this._graphService = graphService != null ? graphService : new GraphService(new Graph(this.api));
        // NOTE: (3) a service to signal loading and stop loading also loading stream.
        this._loadingService = loadingService != null ? loadingService : new LoadingService();
        this._loadingName = "navigator";
        this._stateService = stateService != null ? stateService : new StateService();
        this._cacheService =
            cacheService != null ? cacheService : new CacheService(this._graphService, this._stateService);
        this._playService = playService != null ? playService : new PlayService(this._graphService, this._stateService);
        this._keyRequested$ = new BehaviorSubject(null);
        this._movedToKey$ = new BehaviorSubject(null);
        this._request$ = null;
        this._requestSubscription = null;
        this._nodeRequestSubscription = null;
        this._cameraService =
            cameraService != null ? cameraService : new CameraService(this._stateService, this.api, this._graphService);
        let jumpMode = JumpMode.similar;
        if (options.jumpMode !== undefined) {
            jumpMode = options.jumpMode;
            if (typeof options.jumpMode === "string") {
                jumpMode = JumpMode[options.jumpMode];
            }
            else {
                jumpMode = options.jumpMode;
            }
        }
        this._jumpNodeCalculator = new JumpNodeCalculator(jumpMode);
        this._lookAtService =
            lookAtService != null
                ? lookAtService
                : new LookAtService(this._stateService, this.api, this._graphService, this, this._jumpNodeCalculator);
        this._campaignService =
            campaignService != null
                ? campaignService
                : new CampaignService(this._stateService, this.api, this._graphService, this._jumpNodeCalculator);
        this._epsg = options.epsg;
    }
    get api() {
        return this._api;
    }
    get cacheService() {
        return this._cacheService;
    }
    get graphService() {
        return this._graphService;
    }
    get loadingService() {
        return this._loadingService;
    }
    get movedToKey$() {
        return this._movedToKey$;
    }
    //public get panService(): PanService {
    //    return this._panService;
    //}
    get playService() {
        return this._playService;
    }
    get stateService() {
        return this._stateService;
    }
    get cameraService() {
        return this._cameraService;
    }
    get lookAtService() {
        return this._lookAtService;
    }
    get campaignService() {
        return this._campaignService;
    }
    get jumpNodeCalculator() {
        return this._jumpNodeCalculator;
    }
    get needsResetPanoOrientation() {
        return this._needsResetPanoOrientation;
    }
    set needsResetPanoOrientation(value) {
        this._needsResetPanoOrientation = value;
    }
    get epsg() {
        return this._epsg;
    }
    set epsg(value) {
        this._epsg = value;
    }
    dispose() {
        this._abortRequest("viewer removed");
        this._cacheService.stop();
        this._graphService.dispose();
        this._playService.dispose();
        this._stateService.dispose();
    }
    moveToKey$(key) {
        this._abortRequest(`to key ${key}`);
        // NOTE: (3)
        this._loadingService.startLoading(this._loadingName);
        const node$ = this._moveToKey$(key);
        return this._makeRequest$(node$);
    }
    moveToCamera$(capturesystemID) {
        this._abortRequest(`to camera ${capturesystemID}`);
        // NOTE: (3)
        this._loadingService.startLoading(this._loadingName);
        const node$ = this._cameraService.moveToCamera$(capturesystemID).pipe(mergeMap((cameraKey) => {
            return this._moveToKey$(cameraKey);
        }));
        return this._makeRequest$(node$);
    }
    moveToCampaign$(campaignID) {
        this._abortRequest(`to campaign ${campaignID}`);
        // NOTE: (3)
        this._loadingService.startLoading(this._loadingName);
        const node$ = this._campaignService.moveToCampaign$(campaignID).pipe(mergeMap((key) => {
            return this._moveToKey$(key);
        }));
        return this._makeRequest$(node$);
    }
    moveDir$(direction) {
        this._abortRequest(`in dir ${EdgeDirection[direction]}`);
        this._loadingService.startLoading(this._loadingName);
        const node$ = this.stateService.currentNode$.pipe(first(), mergeMap((node) => {
            return ([EdgeDirection.Next, EdgeDirection.Prev].indexOf(direction) > -1
                ? node.sequenceEdges$
                : node.spatialEdges$).pipe(first(), map((status) => {
                for (const edge of status.edges) {
                    if (edge.data.direction === direction) {
                        return edge.to;
                    }
                }
                return null;
            }));
        }), mergeMap((directionKey) => {
            if (directionKey == null) {
                this._loadingService.stopLoading(this._loadingName);
                return observableThrowError(new Error(`Direction (${direction}) does not exist for current node.`));
            }
            //reset pano orientation on turns
            if ([EdgeDirection.StepLeft, EdgeDirection.StepRight, EdgeDirection.Spherical].indexOf(direction) == -1) {
                this._needsResetPanoOrientation = true;
            }
            return this._moveToKey$(directionKey);
        }));
        return this._makeRequest$(node$);
    }
    movePosition$(latlon, maxdist, sequence) {
        this._abortRequest(`to position ${latlon}`);
        // NOTE: (3)
        this._loadingService.startLoading(this._loadingName);
        const return_error = () => {
            this._loadingService.stopLoading(this._loadingName);
            return observableThrowError(new Error(`No image found at the requested position (${latlon.lat}/${latlon.lon}).`));
        };
        const node$ = this.stateService.currentNode$.pipe(first(), timeoutWith(1, observableOf(null)), mergeMap((node) => {
            return combineLatest([this._movePosition(latlon, node, sequence, maxdist), observableOf(node)]);
        }), mergeMap(([jumpKey, current_node]) => {
            if (jumpKey == null) {
                return this._api.imagesByKNN$(latlon.lon, latlon.lat, 4326).pipe(mergeMap((nearest_node) => {
                    return this._movePosition(nearest_node.l, current_node, sequence, maxdist).pipe(mergeMap((key) => {
                        if (key == null)
                            return return_error();
                        return this._moveToKey$(key);
                    }));
                }));
            }
            else {
                return this._moveToKey$(jumpKey).pipe(catchError((error) => {
                    return return_error();
                }));
            }
        }), catchError((error) => {
            return return_error();
        }));
        return this._makeRequest$(node$);
    }
    _movePosition(latlon, currentNode, sequence, maxdist) {
        const h = this._api.data.geometry.latLonToCellId(latlon);
        const notcacheCell$ = this.api.imagesByH$(h).pipe(mergeMap((imagesByH) => {
            const coreNodes = imagesByH[h];
            const nodes = [];
            for (const index in coreNodes) {
                nodes.push(new Node(coreNodes[index]));
            }
            const node_keys = nodes.map((x) => x.key);
            if (nodes.length == 0)
                return combineLatest([observableOf([]), observableOf({})]);
            return combineLatest([observableOf(nodes), this.api.imageByKeyFill$(node_keys)]);
        }), map(([nodes, fillnodes]) => {
            nodes.map((x) => x.makeFull(fillnodes[x.key]));
            return nodes;
        }));
        const cacheCell$ = this._graphService.cacheCell$(this._api.data.geometry.latLonToCellId(latlon));
        let loadcellnodes$ = cacheCell$;
        if (currentNode) {
            if (this.api.data instanceof Infra3DDataProvider_v2 || this.api.data instanceof Infra3DDataProvider_v3) {
                if (currentNode.frame.collection !== this.api.data.getCollection()) {
                    loadcellnodes$ = notcacheCell$;
                }
            }
        }
        return loadcellnodes$.pipe(mergeMap((node_candidates) => {
            return this._graphService.filter$.pipe(first(), mergeMap((current_filter) => {
                let distance_filtered_candidates = node_candidates;
                if (maxdist) {
                    distance_filtered_candidates = node_candidates.filter((node_candidate) => {
                        const dist = Spatial.distanceFromLatLonAlt(currentNode.frame.latitude, node_candidate.frame.latitude, currentNode.frame.longitude, node_candidate.frame.longitude, currentNode.frame.height, node_candidate.frame.height);
                        if (dist <= maxdist) {
                            return true;
                        }
                        return false;
                    });
                }
                let sequence_filtered_candidates = [];
                if (sequence) {
                    sequence_filtered_candidates = node_candidates.filter((node_candidate) => {
                        if (node_candidate.sequenceKey.indexOf(sequence) != -1) {
                            return true;
                        }
                        return false;
                    });
                }
                let filtered_candidates = [];
                if (sequence_filtered_candidates.length > 0) {
                    filtered_candidates = sequence_filtered_candidates.filter((node_candidate) => current_filter(node_candidate));
                }
                else {
                    filtered_candidates = distance_filtered_candidates.filter((node_candidate) => current_filter(node_candidate));
                }
                if (filtered_candidates.length == 0) {
                    console.warn("There is no frame left after filtering probably due to campaignlock. Filter (incl. Campaign Lock) is beeing ignored!");
                    if (sequence && sequence_filtered_candidates.length != 0) {
                        return observableOf(sequence_filtered_candidates);
                    }
                    return observableOf(distance_filtered_candidates);
                }
                return observableOf(filtered_candidates);
            }));
        }), mergeMap((filtered_nodes) => {
            const potential_nodes = this._jumpNodeCalculator.getPotentialJumpNodes(filtered_nodes, currentNode, latlon);
            return observableOf(this._jumpNodeCalculator.computeSimilarNode(potential_nodes));
        }));
    }
    setFrameFilter$(frame_properties) {
        this._filter_frame_properties = frame_properties;
        return this.stateService.currentNode$.pipe(distinctUntilChanged((node_prev, node) => {
            if (!node_prev.full && node.full)
                return false;
            return frame_properties.every((val) => {
                return eval(`node.${val}`) == eval(`node_prev.${val}`);
            });
        }), map((node) => {
            const new_filter = ["all"];
            for (const frame_property of this._filter_frame_properties) {
                new_filter.push(["==", frame_property, eval(`node.${frame_property}`)]);
            }
            return new_filter;
        }), mergeMap((filter) => this.setFilter$(filter)));
    }
    get filterFrameProperties() {
        return this._filter_frame_properties;
    }
    setFilter$(filter) {
        this._stateService.clearNodes();
        return this._movedToKey$.pipe(first(), mergeMap((key) => {
            if (key != null) {
                return this._trajectoryKeys$().pipe(mergeMap((keys) => {
                    return this._graphService.setFilter$(filter).pipe(mergeMap(() => {
                        return this._cacheKeys$(keys);
                    }));
                }), last());
            }
            return this._keyRequested$.pipe(first(), mergeMap((requestedKey) => {
                if (requestedKey != null) {
                    return this._graphService.setFilter$(filter).pipe(mergeMap(() => {
                        return this._graphService.cacheNode$(requestedKey);
                    }));
                }
                return this._graphService.setFilter$(filter).pipe(map(() => {
                    return undefined;
                }));
            }));
        }), map(() => {
            return undefined;
        }));
    }
    setUserToken$(userToken) {
        this._abortRequest("to set user token");
        this._stateService.clearNodes();
        return this._movedToKey$.pipe(first(), mergeMap((key) => {
            return key == null
                ? this._graphService.reset$([])
                : this._trajectoryKeys$().pipe(mergeMap((keys) => {
                    return this._graphService.reset$(keys).pipe(mergeMap(() => {
                        return this._cacheKeys$(keys);
                    }));
                }), last(), map(() => {
                    return undefined;
                }));
        }));
    }
    setJumpMode(mode) {
        this._jumpNodeCalculator.setJumpMode(mode);
    }
    _cacheKeys$(keys) {
        const cacheNodes$ = keys.map((key) => {
            return this._graphService.cacheNode$(key);
        });
        return observableFrom(cacheNodes$).pipe(mergeAll());
    }
    _abortRequest(reason) {
        if (this._requestSubscription != null) {
            this._requestSubscription.unsubscribe();
            this._requestSubscription = null;
        }
        if (this._nodeRequestSubscription != null) {
            this._nodeRequestSubscription.unsubscribe();
            this._nodeRequestSubscription = null;
        }
        if (this._request$ != null) {
            if (!(this._request$.isStopped || this._request$.hasError)) {
                this._request$.error(new AbortInfra3DSDKError(`Request aborted by a subsequent request ${reason}.`));
            }
            this._request$ = null;
        }
    }
    _makeRequest$(node$) {
        // NOTE: (3)
        const request$ = new ReplaySubject(1);
        this._requestSubscription = request$.subscribe(undefined, () => {
            /*noop*/
        });
        this._request$ = request$;
        this._nodeRequestSubscription = node$.subscribe((node) => {
            this._request$ = null;
            request$.next(node);
            request$.complete();
        }, (error) => {
            this._request$ = null;
            request$.error(error);
        });
        return request$;
    }
    // NOTE: (3)
    _moveToKey$(key) {
        this._keyRequested$.next(key);
        // NOTE: (3)
        return this._graphService.cacheNode$(key).pipe(tap((node) => {
            // NOTE: (3) This is were the image render get notified. via stateService.
            this._stateService.setNodes([node]);
            this._movedToKey$.next(node.key);
        }), mergeMap((node) => {
            return combineLatest([this._graphService.cacheSequenceNodes$(node.sequenceKey), observableOf(node)]);
        }), map(([sequence, node]) => node), finalize(() => {
            this._loadingService.stopLoading(this._loadingName);
        }));
    }
    _trajectoryKeys$() {
        return this._stateService.currentState$.pipe(first(), map((frame) => {
            return frame.state.trajectory.map((node) => {
                return node.key;
            });
        }));
    }
    setAuthObject(authObject) {
        if (this._api.data instanceof Infra3DDataProvider_v2 || this._api.data instanceof Infra3DDataProvider_v3) {
            this._api.data.authObject = authObject;
        }
    }
}
//# sourceMappingURL=Navigator.js.map